import * as React from 'react'
import { useSpinDelay } from 'spin-delay'
import { cn } from '#app/utils/misc.tsx'
import { Button, type ButtonProps } from './button.tsx'
import { Icon } from './icon.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './tooltip.tsx'

export const StatusButton = React.forwardRef<
	HTMLButtonElement,
	ButtonProps & {
		status: 'pending' | 'success' | 'error' | 'idle'
		message?: string | null
		spinDelay?: Parameters<typeof useSpinDelay>[1]
	}
>(({ message, status, className, children, spinDelay, ...props }, ref) => {
	const delayedPending = useSpinDelay(status === 'pending', {
		delay: 400,
		minDuration: 300,
		...spinDelay,
	})

	const statusConfig: Record<
		string,
		{
			icon: 'update' | 'check' | 'cross-1' | null
			iconClass: string
			bgClass: string
		}
	> = {
		pending: {
			icon: 'update',
			iconClass: 'text-primary-foreground',
			bgClass: 'bg-primary',
		},
		success: {
			icon: 'check',
			iconClass: 'text-success-foreground',
			bgClass: 'bg-success',
		},
		error: {
			icon: 'cross-1',
			iconClass: 'text-destructive-foreground',
			bgClass: 'bg-destructive',
		},
		idle: {
			icon: null,
			iconClass: '',
			bgClass: '',
		},
	}

	const { icon, iconClass, bgClass } = statusConfig[status]

	const companion =
		icon && !(status === 'pending' && !delayedPending) ? (
			<div
				role="status"
				className={cn(
					'flex h-6 w-6 items-center justify-center rounded-full',
					bgClass,
				)}
			>
				<Icon
					name={icon}
					className={cn(
						'h-4 w-4',
						iconClass,
						status === 'pending' && 'animate-spin',
					)}
					title={status}
				/>
			</div>
		) : null

	return (
		<Button
			ref={ref}
			className={cn(
				'group relative flex h-12 w-64 items-center justify-center overflow-hidden rounded-md px-4 text-sm font-medium transition-all duration-300 ease-in-out',
				'hover:shadow-md focus:shadow-md',
				'focus:outline-none',
				className,
			)}
			{...props}
		>
			<span className={cn('flex items-center gap-2')}>{children}</span>
			{message ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<div className="flex-shrink-0">{companion}</div>
						</TooltipTrigger>
						<TooltipContent>{message}</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : (
				<div className="flex-shrink-0">{companion}</div>
			)}
			{status !== 'idle' && (
				<div
					className={cn(
						'absolute inset-0 -z-10 opacity-10 transition-opacity duration-300 ease-in-out group-hover:opacity-20',
						bgClass,
					)}
				/>
			)}
		</Button>
	)
})

StatusButton.displayName = 'StatusButton'
